import { NavLink, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useState, useRef, useEffect } from 'react';
import { trackEvent } from './utils/analytics';
import { useAuthenticatedApi } from './api';
import styles from './NavBar.module.css';

export default function NavBar() {
  const { logout, isAuthenticated, user } = useAuth0();
  const { authenticatedFetch } = useAuthenticatedApi();
  const [profilePicture, setProfilePicture] = useState(null);
  const profilePictureUrlRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const handleLogout = () => {
    logout({ 
      logoutParams: { 
        returnTo: window.location.origin,
        clientId: process.env.REACT_APP_AUTH0_CLIENT_ID
      } 
    });
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        closeMobileMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Track screen size for medium screens
  useEffect(() => {
    const handleResize = () => {
      setIsMediumScreen(window.innerWidth <= 1024 && window.innerWidth > 768);
    };

    handleResize(); // Check initial size
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for profile picture updates (e.g. after upload on Profile page)
  useEffect(() => {
    const handler = (e) => {
      const dataUrl = e.detail?.dataUrl;
      if (dataUrl) setProfilePicture(dataUrl);
    };
    window.addEventListener('profilePictureUpdated', handler);
    return () => window.removeEventListener('profilePictureUpdated', handler);
  }, []);

  // Load profile picture for user menu
  useEffect(() => {
    if (!isAuthenticated) {
      if (profilePictureUrlRef.current) {
        URL.revokeObjectURL(profilePictureUrlRef.current);
        profilePictureUrlRef.current = null;
      }
      setProfilePicture(null);
      return;
    }
    let cancelled = false;
    const loadProfilePicture = async () => {
      if (!user?.email || !authenticatedFetch) return;
      try {
        const res = await authenticatedFetch(
          `${process.env.REACT_APP_API_URL || '/api'}/members/profile/picture?email=${encodeURIComponent(user.email)}`
        );
        if (cancelled) return;
        if (res.ok) {
          const blob = await res.blob();
          if (cancelled) return;
          const imageUrl = URL.createObjectURL(blob);
          if (profilePictureUrlRef.current) {
            URL.revokeObjectURL(profilePictureUrlRef.current);
          }
          profilePictureUrlRef.current = imageUrl;
          setProfilePicture(imageUrl);
        }
      } catch {
        if (!cancelled) {
          // Silently fail - will show person icon fallback
        }
      }
    };
    loadProfilePicture();
    return () => {
      cancelled = true;
      // Don't revoke on dep change - would blank the image before new fetch completes.
      // Revoke only when replacing with new URL in the success path.
    };
  }, [isAuthenticated, user?.email, authenticatedFetch]);

  return (
    <nav className={styles.nav}>
      <div className={styles.logoContainer}>
        <Link to="/" onClick={() => trackEvent('nav_home', 'navigation', 'logo')}>
          <img src="/logo.png" alt="Logo" className={styles.logo} />
        </Link>
      </div>
      <div className={styles.container}>
        {/* Desktop Navigation */}
        <div className={styles.linksContainer}>
          <NavLink 
            to="/" 
            className={({ isActive }) => `${styles.link} ${styles.linkHome} ${isActive ? styles.active : ''}`}
            onClick={() => trackEvent('nav_home', 'navigation', 'desktop')}
          >
            🏠 Home
          </NavLink>
          <NavLink 
            to="/opportunities" 
            className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
            onClick={() => trackEvent('nav_opportunities', 'navigation', 'desktop')}
          >
            💼 {isMediumScreen ? 'Opps' : 'Opportunities'}
          </NavLink>
          <NavLink 
            to="/blog" 
            className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
            onClick={() => trackEvent('nav_blog', 'navigation', 'desktop')}
          >
            📝 Blog
          </NavLink>
          <NavLink 
            to="/forum" 
            className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
            onClick={() => trackEvent('nav_forum', 'navigation', 'desktop')}
          >
            💬 Forum
          </NavLink>
          <NavLink 
            to="/classifieds" 
            className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
            onClick={() => trackEvent('nav_classifieds', 'navigation', 'desktop')}
          >
            📋 Classifieds
          </NavLink>
          <NavLink 
            to="/plug-your-work" 
            className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
            onClick={() => trackEvent('nav_plug_your_work', 'navigation', 'desktop')}
          >
            🚀 {isMediumScreen ? 'Plug Work' : 'Plug Your Work'}
          </NavLink>
          <NavLink 
            to="/editor-database" 
            className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
            onClick={() => trackEvent('nav_editor_database', 'navigation', 'desktop')}
          >
            📚 {isMediumScreen ? 'Editor DB' : 'Editor Database'}
          </NavLink>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className={styles.mobileMenuButton}
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span className={styles.hamburger}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

      </div>

      {/* User Menu - Right column */}
      {isAuthenticated && (
        <div className={styles.userMenu} ref={dropdownRef}>
          <button 
            onClick={toggleDropdown}
            className={styles.userMenuButton}
          >
            {profilePicture ? (
              <img src={profilePicture} alt="Profile" className={styles.userAvatar} />
            ) : (
              <span className={styles.userIcon}>👤</span>
            )}
            <span className={styles.dropdownArrow}>▼</span>
          </button>
          {isDropdownOpen && (
            <div className={styles.dropdownMenu}>
              <NavLink 
                to="/profile" 
                className={styles.dropdownLink}
                onClick={() => { trackEvent('nav_profile', 'navigation', 'desktop'); closeDropdown(); }}
              >
                👤 Profile
              </NavLink>
              <NavLink 
                to="/alerts" 
                className={styles.dropdownLink}
                onClick={() => { trackEvent('nav_alerts', 'navigation', 'desktop'); closeDropdown(); }}
              >
                🔔 Alerts
              </NavLink>
              <NavLink 
                to="/account" 
                className={styles.dropdownLink}
                onClick={() => { trackEvent('nav_account', 'navigation', 'desktop'); closeDropdown(); }}
              >
                ⚙️ Account
              </NavLink>
              <a 
                href="mailto:support@studyhall.xyz"
                className={styles.dropdownLink}
                onClick={() => { trackEvent('nav_contact_us', 'navigation', 'desktop'); closeDropdown(); }}
              >
                📧 Contact Us
              </a>
              <button 
                onClick={() => { trackEvent('nav_logout', 'navigation', 'desktop'); handleLogout(); }}
                className={styles.dropdownLogout}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className={styles.mobileMenu} ref={mobileMenuRef}>
          <NavLink 
            to="/" 
            className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}
            onClick={() => { trackEvent('nav_home', 'navigation', 'mobile'); closeMobileMenu(); }}
          >
            🏠 Home
          </NavLink>
          <NavLink 
            to="/opportunities" 
            className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}
            onClick={() => { trackEvent('nav_opportunities', 'navigation', 'mobile'); closeMobileMenu(); }}
          >
            💼 Opportunities
          </NavLink>
          <NavLink 
            to="/blog" 
            className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}
            onClick={() => { trackEvent('nav_blog', 'navigation', 'mobile'); closeMobileMenu(); }}
          >
            📝 Blog
          </NavLink>
          <NavLink 
            to="/forum" 
            className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}
            onClick={() => { trackEvent('nav_forum', 'navigation', 'mobile'); closeMobileMenu(); }}
          >
            💬 Forum
          </NavLink>
          <NavLink 
            to="/classifieds" 
            className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}
            onClick={() => { trackEvent('nav_classifieds', 'navigation', 'mobile'); closeMobileMenu(); }}
          >
            📋 Classifieds
          </NavLink>
          <NavLink 
            to="/plug-your-work" 
            className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}
            onClick={() => { trackEvent('nav_plug_your_work', 'navigation', 'mobile'); closeMobileMenu(); }}
          >
            🚀 Plug Your Work
          </NavLink>
          <NavLink 
            to="/editor-database" 
            className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}
            onClick={() => { trackEvent('nav_editor_database', 'navigation', 'mobile'); closeMobileMenu(); }}
          >
            📚 Editor Database
          </NavLink>
          {isAuthenticated && (
            <>
              <div className={styles.mobileMenuDivider}></div>
              <NavLink 
                to="/profile" 
                className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}
                onClick={() => { trackEvent('nav_profile', 'navigation', 'mobile'); closeMobileMenu(); }}
              >
                👤 Profile
              </NavLink>
              <NavLink 
                to="/alerts" 
                className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}
                onClick={() => { trackEvent('nav_alerts', 'navigation', 'mobile'); closeMobileMenu(); }}
              >
                🔔 Alerts
              </NavLink>
              <NavLink 
                to="/account" 
                className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileActive : ''}`}
                onClick={() => { trackEvent('nav_account', 'navigation', 'mobile'); closeMobileMenu(); }}
              >
                ⚙️ Account
              </NavLink>
              <a 
                href="mailto:support@studyhall.xyz"
                className={styles.mobileLink}
                onClick={() => { trackEvent('nav_contact_us', 'navigation', 'mobile'); closeMobileMenu(); }}
              >
                📧 Contact Us
              </a>
              <button 
                onClick={() => { trackEvent('nav_logout', 'navigation', 'mobile'); handleLogout(); closeMobileMenu(); }}
                className={styles.mobileLogout}
              >
                🚪 Logout
              </button>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
